/**
 * useIdleTimer
 * Tracks user activity and fires a warning, then onIdle, after a period of
 * inactivity. Once the warning is showing, background activity no longer
 * resets the timer - only an explicit call to `stayLoggedIn()` does.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const

interface UseIdleTimerOptions {
  timeoutMs: number
  warningMs: number
  onIdle: () => void
  enabled: boolean
}

interface UseIdleTimerResult {
  isWarning: boolean
  remainingSeconds: number
  stayLoggedIn: () => void
}

export function useIdleTimer({
  timeoutMs,
  warningMs,
  onIdle,
  enabled,
}: UseIdleTimerOptions): UseIdleTimerResult {
  const [isWarning, setIsWarning] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(Math.ceil(warningMs / 1000))

  const isWarningRef = useRef(false)
  const onIdleRef = useRef(onIdle)
  const idleTimeoutId = useRef<ReturnType<typeof setTimeout>>()
  const warningTimeoutId = useRef<ReturnType<typeof setTimeout>>()
  const countdownIntervalId = useRef<ReturnType<typeof setInterval>>()

  useEffect(() => {
    onIdleRef.current = onIdle
  }, [onIdle])

  useEffect(() => {
    isWarningRef.current = isWarning
  }, [isWarning])

  const clearTimers = useCallback(() => {
    clearTimeout(idleTimeoutId.current)
    clearTimeout(warningTimeoutId.current)
    clearInterval(countdownIntervalId.current)
  }, [])

  const startTimers = useCallback(() => {
    clearTimers()
    setIsWarning(false)
    setRemainingSeconds(Math.ceil(warningMs / 1000))

    warningTimeoutId.current = setTimeout(() => {
      setIsWarning(true)
      let secondsLeft = Math.ceil(warningMs / 1000)
      setRemainingSeconds(secondsLeft)

      countdownIntervalId.current = setInterval(() => {
        secondsLeft -= 1
        setRemainingSeconds(Math.max(0, secondsLeft))
      }, 1000)
    }, Math.max(0, timeoutMs - warningMs))

    idleTimeoutId.current = setTimeout(() => {
      clearTimers()
      onIdleRef.current()
    }, timeoutMs)
  }, [timeoutMs, warningMs, clearTimers])

  useEffect(() => {
    if (!enabled) {
      clearTimers()
      setIsWarning(false)
      return
    }

    startTimers()

    const handleActivity = () => {
      if (!isWarningRef.current) {
        startTimers()
      }
    }

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }))

    return () => {
      clearTimers()
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity))
    }
  }, [enabled, startTimers, clearTimers])

  const stayLoggedIn = useCallback(() => {
    startTimers()
  }, [startTimers])

  return { isWarning, remainingSeconds, stayLoggedIn }
}
